<template>
  <div class="flex-grow-1 d-flex flex-column overflow-y-hidden">
    <div class="a-header">
      <span class="a-header--title slide-down-enter-active">
        Finetune Suggestions
      </span>
    </div>
    <div v-if="isLoading" class="d-flex justify-content-center align-items-center pt-35-px">
      <Spinner />
    </div>
    <transition name="slide-up">
      <div v-if="!isLoading" class="a-filter">
        <a-row class="full-w" type="flex" justify="start" :gutter="24">
          <a-col :span="8">
            <div class="full-w a-input-group" :class="{ active: !!filterParams.search }">
              <a-button size="large">
                <a-icon type="search" />
              </a-button>
              <a-input class="custom-prefix" placeholder="Search" @input="onSearch" />
            </div>
          </a-col>
          <a-col :span="8">
            <div
              class="full-w a-input-group"
              :class="{ active: !!filterParams.fromDate && !!filterParams.toDate }"
            >
              <a-button size="large">
                <a-icon type="filter" />
              </a-button>
              <a-range-picker
                :ranges="RANGE"
                :placeholder="['From', 'To']"
                :format="DATE_FORMAT.DDMMMYYYY"
                @change="onChangeDate"
              />
            </div>
          </a-col>
          <a-col :span="8">
            <a-select
              v-model="filterParams.statuses"
              mode="multiple"
              allowClear
              size="large"
              class="full-w"
              placeholder="Filter by status"
              @change="onStatusChange"
            >
              <a-select-option
                v-for="status in statusOptions"
                :key="status.value"
                :value="status.value"
              >
                {{ status.label }}
              </a-select-option>
            </a-select>
          </a-col>
        </a-row>
        <a-row class="full-w mt-2" type="flex" justify="start">
          <a-button
            size="default"
            :disabled="!metadata || !isHasSelected || isBatchDeleting"
            :loading="isBatchDeleting"
            type="danger"
            class="danger-btn"
            @click="onBatchDelete"
          >
            <a-icon type="delete" />
            Delete Selected
          </a-button>
        </a-row>
      </div>
    </transition>
    <div v-resize="elSizeChange" class="a-body flex-grow-1 overflow-y-hidden">
      <transition name="slide-up">
        <template v-if="!isLoading">
          <PaginationTable
            :clientPaging="false"
            :hasSelectRow="true"
            :pagination="pagin"
            :columns="FinetuneSuggestionListColumn"
            :dataSource="filterData"
            :tableSize="tableSize"
            @changeSelectedRow="onSelectChange"
          >
            <template slot="jobId" slot-scope="{ record }">
              <router-link
                class="nav-link-detail"
                :to="{
                  name: autoFinetuneRouteNames.SUGGESTION_DETAIL,
                  query: detailQueryFromRecord(record)
                }"
              >
                {{ record.id }}
              </router-link>
            </template>
            <template slot="status" slot-scope="{ record }">
              <router-link
                v-if="canViewDetail(record)"
                class="nav-link-detail d-inline-block"
                :to="{
                  name: autoFinetuneRouteNames.SUGGESTION_DETAIL,
                  query: detailQueryFromRecord(record)
                }"
              >
                <a-tag :color="statusTagColor(record.status)">
                  {{ displayStatus(record.status) }}
                </a-tag>
              </router-link>
              <a-tag v-else :color="statusTagColor(record.status)">
                {{ displayStatus(record.status) }}
              </a-tag>
            </template>
            <template slot="analysisWindow" slot-scope="{ record }">
              <span>{{ formatAnalysisWindow(record) }}</span>
            </template>
            <template slot="created_at" slot-scope="{ record }">
              <div>
                <p class="time-text">
                  {{ formatListDate(record.created_at) }}
                </p>
                <p class="time-text">
                  {{ formatListTime(record.created_at) }}
                </p>
              </div>
            </template>
            <template slot="completed_at" slot-scope="{ record }">
              <div v-if="record.completed_at">
                <p class="time-text">
                  {{ formatListDate(record.completed_at) }}
                </p>
                <p class="time-text">
                  {{ formatListTime(record.completed_at) }}
                </p>
              </div>
              <span v-else>
                -
              </span>
            </template>
            <template slot="operation" slot-scope="{ record }">
              <a-dropdown v-if="canViewDetail(record) || canDeleteJob(record)" placement="topLeft">
                <a-icon class="operation" type="ellipsis" />
                <a-menu slot="overlay" class="operation-menu">
                  <a-menu-item v-if="canViewDetail(record)" @click="viewDetail(record)">
                    <a-icon type="eye" />
                    <span>View Detail</span>
                  </a-menu-item>
                  <a-menu-item
                    v-if="!metadata || canDeleteJob(record)"
                    :disabled="!metadata || !!deletingJobIds[record.id]"
                  >
                    <div class="full-wh text-danger" @click="onDeleteJob(record)">
                      <a-icon type="delete" />
                      Delete
                    </div>
                  </a-menu-item>
                </a-menu>
              </a-dropdown>
              <span v-else class="text-muted">
                —
              </span>
            </template>
          </PaginationTable>
        </template>
      </transition>
    </div>
    <div class="a-footer">
      <PaginationBar :pageSize.sync="pagin.pageSize" :total="pagin.total" @change="paginChange" />
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash';
import { DATE_FORMAT } from '@constants/dateFormat';
import { RANGE } from '@constants/range';
import {
  accountDateToUtc,
  formatAccountDate,
  formatAccountTime,
  formatAnalysisWindow,
  resolveAccountTimezone
} from '@modules/AutoFinetune/helpers/dateTime';
import { fetchAutoFinetuneMetadata } from '@modules/AutoFinetune/helpers/metadata';
import {
  getJobStatusColor,
  getJobStatusLabel,
  getJobStatusOptions,
  isJobStatusDeletable,
  isJobStatusPollable
} from '@modules/AutoFinetune/helpers/status';
import { AUTO_FINETUNE_ROUTE_NAMES } from '@modules/AutoFinetune/router/routes';
import { getApiErrorMessage } from '@shared/utils/finetune';
import { FinetuneSuggestionListColumn } from '../columnsTable/FinetuneSuggestionListColumn';
import FinetuneSuggestionService from '../services/FinetuneSuggestion.service';

const POLL_INTERVAL_MS = 5000;

export default {
  name: 'FinetuneSuggestionList',
  data() {
    let pageSize = Number(window.localStorage.getItem('pageSize'));
    if (!pageSize) pageSize = 5;

    return {
      RANGE,
      DATE_FORMAT,
      autoFinetuneRouteNames: AUTO_FINETUNE_ROUTE_NAMES,
      FinetuneSuggestionListColumn,
      metadata: null,
      filterData: [],
      filterParams: {
        search: '',
        fromDate: null,
        toDate: null,
        statuses: []
      },
      pagin: {
        current: 1,
        pageSize,
        total: 0
      },
      isLoading: true,
      tableSize: {
        height: '',
        width: ''
      },
      pollTimerId: null,
      deletingJobIds: {},
      selectedRowKeys: [],
      isBatchDeleting: false
    };
  },
  computed: {
    isHasSelected() {
      return this.selectedRowKeys.length > 0;
    },
    statusOptions() {
      return getJobStatusOptions(this.metadata);
    },
    maximumBatchDeleteJobs() {
      const maximum =
        this.metadata &&
        this.metadata.limits &&
        this.metadata.limits.max_batch_delete_jobs;
      return maximum != null ? Number(maximum) : null;
    },
    custTimezone() {
      return resolveAccountTimezone(this.$store);
    }
  },
  mounted() {
    this.loadMetadata();
    this.fetchList().finally(() => {
      this.startPolling();
    });
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    async loadMetadata() {
      try {
        this.metadata = await fetchAutoFinetuneMetadata();
        this.startPolling();
      } catch (error) {
        this.metadata = null;
        this.stopPolling();
      }
    },
    startPolling() {
      this.stopPolling();
      if (
        !this.metadata ||
        !this.filterData.some((record) =>
          isJobStatusPollable(this.metadata, record.status)
        )
      ) {
        return;
      }
      this.pollTimerId = setInterval(() => {
        this.fetchList({ silent: true });
      }, POLL_INTERVAL_MS);
    },
    stopPolling() {
      if (this.pollTimerId != null) {
        clearInterval(this.pollTimerId);
        this.pollTimerId = null;
      }
    },
    statusTagColor(status) {
      return getJobStatusColor(this.metadata, status);
    },
    displayStatus(status) {
      return getJobStatusLabel(this.metadata, status);
    },
    canViewDetail(record) {
      return !!(record && record.id);
    },
    canDeleteJob(record) {
      return !!(
        this.metadata &&
        record &&
        isJobStatusDeletable(this.metadata, record.status)
      );
    },
    detailQueryFromRecord(record) {
      return { job_id: record.id };
    },
    viewDetail(record) {
      if (!this.canViewDetail(record)) return;
      this.$router.push({
        name: AUTO_FINETUNE_ROUTE_NAMES.SUGGESTION_DETAIL,
        query: this.detailQueryFromRecord(record)
      });
    },
    formatAnalysisWindow(record) {
      return formatAnalysisWindow(
        record && record.start_datetime,
        record && record.end_datetime,
        this.custTimezone,
        '-'
      );
    },
    formatListDate(value) {
      return formatAccountDate(value, this.custTimezone, '-');
    },
    formatListTime(value) {
      return formatAccountTime(value, this.custTimezone, '-');
    },
    elSizeChange(size) {
      this.tableSize = size;
    },
    onSelectChange(selectedRowKeys) {
      this.selectedRowKeys = selectedRowKeys || [];
    },
    onChangeDate(dates, dateStrings) {
      this.filterParams.fromDate = dateStrings[0]
        ? accountDateToUtc(dateStrings[0], 'DD MMM YYYY', this.custTimezone)
        : null;
      this.filterParams.toDate = dateStrings[1]
        ? accountDateToUtc(
            dateStrings[1],
            'DD MMM YYYY',
            this.custTimezone,
            true
          )
        : null;
      this.pagin.current = 1;
      this.selectedRowKeys = [];
      this.fetchList();
    },
    onStatusChange() {
      this.pagin.current = 1;
      this.selectedRowKeys = [];
      this.fetchList();
    },
    onSearch: debounce(function (inputChangeEvent) {
      const inputSearchValue = inputChangeEvent.target.value;
      this.filterParams.search = inputSearchValue ? String(inputSearchValue).trim() : '';
      this.pagin.current = 1;
      this.selectedRowKeys = [];
      this.fetchList();
    }, 800),
    paginChange(pagin) {
      this.pagin = { ...pagin };
      this.selectedRowKeys = [];
      this.fetchList();
    },
    onDeleteJob(record) {
      if (!this.canDeleteJob(record) || this.deletingJobIds[record.id]) {
        return;
      }
      this.$confirm({
        title: 'Delete this suggestion?',
        content: `Analysis window ${this.formatAnalysisWindow(
          record
        )} will be permanently deleted.`,
        okText: 'Delete',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk: () => this.performDeleteJob(record)
      });
    },
    async performDeleteJob(record) {
      if (!record || !record.id || this.deletingJobIds[record.id]) {
        return;
      }
      this.$set(this.deletingJobIds, record.id, true);
      try {
        await FinetuneSuggestionService.deleteJob(record.id);
        this.$message.success('Suggestion deleted.');
        this.selectedRowKeys = this.selectedRowKeys.filter((id) => id !== record.id);
        if (this.filterData.length <= 1 && this.pagin.current > 1) {
          this.pagin.current -= 1;
        }
        await this.fetchList();
      } catch (e) {
        this.$message.error(
          getApiErrorMessage(e, 'Failed to delete suggestion.')
        );
      } finally {
        this.$delete(this.deletingJobIds, record.id);
      }
    },
    onBatchDelete() {
      const selectedRecords = this.filterData.filter((item) => this.selectedRowKeys.includes(item.id));
      const deletableRecords = selectedRecords.filter((item) => this.canDeleteJob(item));
      const skippedCount = selectedRecords.length - deletableRecords.length;

      if (!deletableRecords.length) {
        this.$message.warning(
          skippedCount > 0
            ? 'Only suggestions marked as deletable can be deleted.'
            : 'No suggestions selected.'
        );
        return;
      }
      if (
        this.maximumBatchDeleteJobs != null &&
        deletableRecords.length > this.maximumBatchDeleteJobs
      ) {
        this.$message.warning(
          `Select no more than ${this.maximumBatchDeleteJobs} suggestions at a time.`
        );
        return;
      }

      const contentParts = [
        `${deletableRecords.length} suggestion(s) will be permanently deleted.`
      ];
      if (skippedCount > 0) {
        contentParts.push(`${skippedCount} non-deletable item(s) will be skipped.`);
      }

      this.$confirm({
        title: `Delete ${deletableRecords.length} suggestion(s)?`,
        content: contentParts.join(' '),
        okText: 'Delete',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk: () => this.performBatchDelete(deletableRecords.map((item) => item.id))
      });
    },
    async performBatchDelete(ids) {
      if (!ids.length || this.isBatchDeleting) return;
      this.isBatchDeleting = true;
      try {
        const response = await FinetuneSuggestionService.batchDeleteJobs(ids);
        const deleted = (response && response.deleted) || [];
        const failed = (response && response.failed) || [];
        if (deleted.length > 0) {
          this.$message.success(
            (response && response.message) || `Deleted ${deleted.length} suggestion(s).`
          );
        }
        if (failed.length > 0) {
          this.$message.warning(`${failed.length} suggestion(s) could not be deleted.`);
        }
        this.selectedRowKeys = [];
        if (deleted.length > 0 && this.filterData.length <= deleted.length && this.pagin.current > 1) {
          this.pagin.current -= 1;
        }
        await this.fetchList();
      } catch (e) {
        this.$message.error(
          getApiErrorMessage(e, 'Failed to delete suggestions.')
        );
      } finally {
        this.isBatchDeleting = false;
      }
    },
    async fetchList(options = {}) {
      const silent = Boolean(options && options.silent);

      try {
        const response = await FinetuneSuggestionService.fetchJobs(
          this.pagin.current,
          this.pagin.pageSize,
          this.filterParams.search,
          this.filterParams.fromDate,
          this.filterParams.toDate,
          this.filterParams.statuses
        );
        const rows = (response && response.data) || [];
        const total = (response && response.total) != null ? response.total : 0;
        const selectedIdSet = new Set(this.selectedRowKeys);
        this.filterData = rows.map((item) => ({
          id: item.id,
          start_datetime: item.start_datetime,
          end_datetime: item.end_datetime,
          created_at: item.created_at,
          completed_at: item.completed_at,
          status: item.status,
          isChecked: selectedIdSet.has(item.id)
        }));
        this.pagin.total = total;
        // Drop selections that are no longer on the current page after refresh.
        const pageIds = new Set(this.filterData.map((item) => item.id));
        this.selectedRowKeys = this.selectedRowKeys.filter((id) => pageIds.has(id));
        this.startPolling();
      } catch (e) {
        if (!silent) {
          this.filterData = [];
          this.pagin.total = 0;
          this.selectedRowKeys = [];
          this.$message.error(
            getApiErrorMessage(e, 'Failed to load auto-finetune jobs.')
          );
        }
      } finally {
        if (this.isLoading) {
          setTimeout(() => {
            this.isLoading = false;
          }, 300);
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.time-text {
  margin-bottom: 0;
  white-space: nowrap;
}

.nav-link-detail {
  &:hover {
    text-decoration: underline;
  }
}

.danger-btn {
  &:disabled {
    opacity: 0.6;
  }
}
</style>
